import * as React from 'react';
import { Box, Chip } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { CustomButton } from '../../custom-forms';
import { useStyles, cx } from '../../custom-forms';
import {
  ColumnFilter,
  FiltersStateFor,
  isDateRangeFilter,
  isRangeFilter
} from '../../types/grid-filter.types';

export interface FilterChipsProps<TKey extends string> {
  filters: FiltersStateFor<TKey>;
  /** Display label of a filter value (select/object filters); range & date are formatted here. */
  filterLabel: (key: TKey, filter: ColumnFilter | null) => string;
  /** Localized column header. */
  columnLabel: (key: TKey) => string;
  clearAllLabel: string;
  onRemove: (key: TKey) => void;
  onClearAll: () => void;
}

/** Active grid-filter chips (reusable across any grid built on `grid-filter.types`). */
function FilterChips<TKey extends string>({
  filters,
  filterLabel,
  columnLabel,
  clearAllLabel,
  onRemove,
  onClearAll
}: FilterChipsProps<TKey>) {
  const c = useStyles();
  const active = Object.keys(filters) as TKey[];
  if (!active.length) return null;

  return (
    <Box className={cx(c.row, c.wrap, c.gap8, c.mb10)}>
      {active.map((key) => {
        const filter = filters[key];
        const text =
          filter && isRangeFilter(filter)
            ? `${filter.min ?? ''}–${filter.max ?? ''}`
            : filter && isDateRangeFilter(filter)
            ? `${filter.from ?? ''}–${filter.to ?? ''}`
            : filterLabel(key, filter ?? null);
        return (
          <Chip
            key={key}
            label={`${columnLabel(key)}: ${text}`}
            onDelete={() => onRemove(key)}
            deleteIcon={<CloseIcon style={{ fontSize: 17, color: '#2E9A91' }} />}
            style={{
              backgroundColor: '#EFF9F8',
              color: '#2E9A91',
              border: '1px solid #CDE9E6',
              borderRadius: 20,
              fontSize: 13,
              fontWeight: 600
            }}
          />
        );
      })}
      <CustomButton
        variant="outlined"
        size="medium"
        onClick={onClearAll}
        style={{ backgroundColor: '#fff', borderColor: '#E4E7F0', color: '#5A6878' }}
      >
        {clearAllLabel}
      </CustomButton>
    </Box>
  );
}

export default FilterChips;
